import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import Form from "../carbon/Form/Form";
import ContactRow from "./ContactRow";
import {
  ContactsFormFields,
  ContactFormFields,
  contactsValidationSchema,
} from "./contactsInformationValidationSchema";
import { CompanyProfileRequest, CompanyProfileResponse } from "../../types";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import "./contactsInformationForm.scss";

interface ContactsInformationFormProps {
  profile: CompanyProfileResponse;
  setIsDirty: (isDirty: boolean) => void;
}

const emptyContact: ContactFormFields = {
  name: "",
  email: "",
  phone: "",
  position: "",
  type: "",
};

/**
 * The Contacts wizard sub-form (`PUT /company/{id}/profile`), rebuilt on Carbon + React Hook
 * Form's `useFieldArray` (Task 10, Profile/Company flow) — the SAME
 * Addresses/BankingInformation/Certifications (Tasks 8-9) rewrite shape: ONE `contacts` array,
 * seeded from `profile.contacts`, editable in place via `useFieldArray` (`append`/`remove`),
 * submitted whole. Real behaviour change from the pre-Carbon form (which only ever let you ADD
 * new rows or remove an unsaved one — an already-persisted contact was a permanently read-only
 * card), safe for the identical reason Task 8/9 already established: `CompanyService#
 * updateCompanyContacts` (backend) already fully REPLACES the contacts list on every save
 * (`company.getContacts().clear()` then re-adds from the request).
 *
 * `onSubmit` spreads `{...profile, ...}` (not just `{contacts}`), same as every other wizard
 * sub-form — `taxId` is `@NotBlank` on the backend (Task 2), and every independent save must keep
 * carrying it.
 */
const ContactsInformationForm = ({
  profile,
  setIsDirty,
}: ContactsInformationFormProps) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const defaultValues: ContactsFormFields = useMemo(
    () => ({
      contacts: (profile.contacts || []).map((contact) => ({
        id: contact.id,
        name: contact.name ?? "",
        email: contact.email ?? "",
        phone: contact.phone ?? "",
        position: contact.position ?? "",
        type: contact.type ?? "",
      })),
    }),
    [profile]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ContactsFormFields>({
    resolver: yupResolver(contactsValidationSchema),
    defaultValues,
  });

  // Same re-sync + one-directional dirty-forwarding precedent as AddressesForm.tsx/
  // BasicInformationForm.tsx — see those files' own docstrings/comments for the full rationale.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (isDirty) {
      setIsDirty(true);
    }
  }, [isDirty, setIsDirty]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contacts",
  });

  const { mutate: updateProfile, isPending: isSaving } =
    useUpdateCompanyProfile(() => {
      setIsDirty(false);
    });

  const onSubmit = (data: ContactsFormFields) => {
    updateProfile({
      ...profile,
      companyId: user?.company?.id,
      contacts: data.contacts,
    } as CompanyProfileRequest);
  };

  return (
    <div className="contacts-information-form">
      <div className="contacts-information-form__header">
        <h3>{t("companyProfile.contacts")}</h3>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Add}
          type="button"
          onClick={() => append(emptyContact)}
        >
          {t("companyProfile.addContact")}
        </Button>
      </div>

      {isSaving ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="contacts-information-form__form"
        >
          {fields.length > 0 && (
            <div className="contacts-information-form__rows">
              {fields.map((field, index) => (
                <ContactRow
                  key={field.id}
                  index={index}
                  control={control}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          )}

          <div className="contacts-information-form__actions">
            <Button type="submit" disabled={isSaving || !isDirty}>
              {t("cta.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default ContactsInformationForm;
